'use client';

import React from 'react';
import Icon from '@/components/ui/AppIcon';
import type { RequestFormData } from './ServiceRequestWizard';

interface SubmissionSuccessProps {
  formData: RequestFormData;
  onNewRequest: () => void;
}

const serviceLabelMap: Record<string, string> = {
  cleaning: 'Cleaning',
  gardening: 'Gardening & Lawn',
  handyman: 'Handyman & Odd Jobs',
};

const cleaningSubLabelMap: Record<string, string> = {
  house: 'House / Unit Cleaning',
  driveway: 'Driveway / Garage Floor',
  pool: 'Pool / Spa Cleaning',
};

export default function SubmissionSuccess({ formData, onNewRequest }: SubmissionSuccessProps) {
  const serviceLabel = serviceLabelMap[formData.serviceCategory] ?? formData.serviceCategory;
  const subLabel = formData.cleaningSubType ? cleaningSubLabelMap[formData.cleaningSubType] : null;
  const refNumber = 'SQ-2026-08114';

  return (
    <div className="card-surface p-6 sm:p-8 space-y-6 animate-slide-up">
      {/* Success icon */}
      <div className="flex flex-col items-center text-center gap-4">
        <div className="w-16 h-16 bg-secondary rounded-full flex items-center justify-center">
          <Icon name="CheckCircleIcon" size={36} variant="outline" className="text-primary" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-foreground">Request submitted!</h2>
          <p className="text-sm text-muted-foreground mt-1">
            We've received your service request and will send a quote to{' '}
            <span className="font-medium text-foreground">{formData.email || 'your email'}</span> within 24 hours.
          </p>
        </div>
      </div>

      {/* Reference number */}
      <div className="bg-muted/40 rounded-xl p-4 text-center">
        <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Request Reference</p>
        <p className="text-xl font-bold text-primary font-tabular mt-1">{refNumber}</p>
        <p className="text-xs text-muted-foreground mt-1">Keep this for your records</p>
      </div>

      {/* Summary */}
      <div className="space-y-2">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Request Summary</p>
        <div className="divide-y divide-border">
          {[
            { label: 'Service', value: subLabel ? `${serviceLabel} — ${subLabel}` : serviceLabel },
            { label: 'Property', value: formData.propertyAddress || '—' },
            { label: 'Preferred date', value: formData.preferredDate1 || '—' },
            { label: 'Contact', value: formData.firstName ? `${formData.firstName} ${formData.lastName}` : '—' },
          ].map((row) => (
            <div key={`summary-${row.label.replace(/\s/g, '-')}`} className="flex items-start justify-between py-2 gap-4">
              <span className="text-xs text-muted-foreground flex-shrink-0">{row.label}</span>
              <span className="text-xs font-medium text-foreground text-right">{row.value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* What happens next */}
      <div className="space-y-2">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">What happens next</p>
        <div className="space-y-2.5">
          {[
            { icon: 'EnvelopeIcon', text: 'Check your email — your personalised quote will arrive within 24 hours.' },
            { icon: 'CursorArrowRaysIcon', text: "Click 'Accept Quote' in the email to confirm your booking." },
            { icon: 'UserCircleIcon', text: "We'll create your client account and send login details so you can track your job." },
          ].map((item, i) => (
            <div key={`next-step-${i + 1}`} className="flex items-start gap-3">
              <div className="w-7 h-7 bg-secondary rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <Icon name={item.icon as any} size={14} variant="outline" className="text-primary" />
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">{item.text}</p>
            </div>
          ))}
        </div>
      </div>

      <button
        type="button"
        onClick={onNewRequest}
        className="w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 border border-border text-sm font-medium text-muted-foreground rounded-lg hover:bg-muted hover:text-foreground transition-all duration-150"
      >
        <Icon name="PlusIcon" size={16} variant="outline" />
        Submit another request
      </button>
    </div>
  );
}